import { useRef, useState } from "react";
import { useParams, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { type Locale } from "@/shared/utils";
import { MembersSection, TaskCalendar } from "@/shared/components";
import { useAuth, useBreadcrumbOverrides } from "@/shared/hooks";
import { PERMISSIONS } from "@/shared/consts/permissions";
import { useGetProjectQuery, useGetProjectMembersQuery, useGetBoardQuery } from "../../api";
import { MembersModal } from "../../components";
import { PROJECT_STATUS_MAP, CATEGORY_MAP } from "../../utils";
import {
  HeroSection,
  KpiSection,
  RecentTasksSection,
  BudgetSection,
  SimilarProjectsSection,
  ProjectSidebar,
  DetailSkeleton,
} from "./components";

export default function ProjectDetailPage() {
  const { t, i18n } = useTranslation("ProjectsPage");
  const locale = (i18n.language as Locale) || "ru";
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const projectId = Number(id);

  const { hasPermission } = useAuth();
  const canEdit = hasPermission(PERMISSIONS.PROJECTS.EDIT_PROJECT);

  const heroRef = useRef<HTMLDivElement>(null);
  const kpiRef = useRef<HTMLDivElement>(null);
  const tasksRef = useRef<HTMLDivElement>(null);
  const membersRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const budgetRef = useRef<HTMLDivElement>(null);
  const similarRef = useRef<HTMLDivElement>(null);

  const [showMembersModal, setShowMembersModal] = useState(false);

  const {
    data: project,
    isLoading,
    error,
  } = useGetProjectQuery(projectId, {
    skip: isNaN(projectId),
  });

  const { data: members = [] } = useGetProjectMembersQuery(projectId, {
    skip: isNaN(projectId),
  });

  const { data: board } = useGetBoardQuery(projectId, {
    skip: isNaN(projectId),
  });

  useBreadcrumbOverrides(project ? { projectName: project.name } : {});

  if (isLoading) {
    return (
      <>
        <title>{t("meta.detailTitle")}</title>
        <section className="flex-1">
            <div className="flex items-center justify-between pb-4 border-b surface-base-stroke mb-5">
              <h1 className="text-display-xs content-base-primary">{t("page.detailTitle")}</h1>
            </div>
            <DetailSkeleton />
        </section>
      </>
    );
  }

  if (error || !project) {
    return (
      <>
        <title>{t("meta.detailTitle")}</title>
        <section className="flex-1">
            <div className="flex items-center justify-between pb-4 border-b surface-base-stroke mb-5">
              <h1 className="text-display-xs content-base-primary">{t("page.detailTitle")}</h1>
            </div>
            <p className="mt-4 text-body-regular-md content-action-negative">{t("page.error")}</p>
        </section>
      </>
    );
  }

  const statusInfo = PROJECT_STATUS_MAP[project.status];
  const categoryEntry = project.category ? CATEGORY_MAP[project.category] : null;
  const categoryLabel = categoryEntry ? t(categoryEntry.label) : "";

  const allBoardTasks = board?.columns.flatMap((col) => col.tasks) ?? [];
  const boardTasksById = new Map(allBoardTasks.map((task) => [task.id, task]));
  const enrichedRecentTasks = project.recent_tasks.map((task) => {
    const boardTask = boardTasksById.get(task.id);
    return boardTask ? { ...task, due_date: boardTask.due_date } : task;
  });
  const enrichedKpis = project.kpis.map((kpi) => {
    const children = allBoardTasks.filter((t) => t.parent_task === kpi.id);
    return {
      ...kpi,
      subtask_count: children.length > 0 ? children.length : kpi.first_level_tasks_total,
      subtasks_done: children.length > 0 ? children.filter((c) => c.status === "done").length : kpi.first_level_tasks_done,
    };
  });

  return (
    <>
      <title>{`${project.name} — ${t("meta.detailTitle")}`}</title>
      <section className="flex-1">
          <div className="flex justify-start items-start gap-5">
            <div className="flex flex-col flex-1 gap-3">
              <div ref={heroRef}>
                <HeroSection
                  project={project}
                  statusInfo={statusInfo}
                  categoryLabel={categoryLabel}
                  categoryIcon={categoryEntry?.icon}
                  t={t}
                />
              </div>

              {enrichedKpis.length > 0 && (
                <div ref={kpiRef}>
                  <KpiSection
                    kpis={enrichedKpis}
                    t={t}
                    onKpiClick={(kpiId) => navigate(`/board?project=${projectId}&kpi=${kpiId}&tab=kanban`)}
                  />
                </div>
              )}

              <div ref={tasksRef}>
                <RecentTasksSection
                  tasks={enrichedRecentTasks}
                  projectId={projectId}
                  projectName={project.name}
                  t={t}
                  onTaskClick={(task) => navigate(`/board?project=${projectId}&taskId=${task.id}`)}
                />
              </div>

              <div ref={membersRef}>
                <MembersSection
                  members={members.map((m) => ({ id: m.id, name: m.name, job_position_ru: m.job_position_ru, job_position_kk: m.job_position_kk }))}
                  canEdit={canEdit}
                  onOpenModal={() => setShowMembersModal(true)}
                  locale={locale}
                />
              </div>

              {canEdit && !project.is_internal && project.budget_summary && (
                <div ref={budgetRef}>
                  <BudgetSection summary={project.budget_summary} project={project} projectId={projectId} locale={locale} t={t} />
                </div>
              )}

              <div ref={calendarRef}>
                <TaskCalendar
                  tasks={allBoardTasks}
                  locale={locale}
                  onTaskClick={(task) => navigate(`/board?project=${projectId}&taskId=${task.id}`)}
                  title={t("detail.calendarTitle")}
                />
              </div>

              {project.similar_projects.length > 0 && (
                <div ref={similarRef}>
                  <SimilarProjectsSection
                    projects={project.similar_projects}
                    locale={locale}
                    t={t}
                    onProjectClick={(pid) => navigate(`/projects/${pid}`)}
                  />
                </div>
              )}
            </div>

            <ProjectSidebar
              project={project}
              canEdit={canEdit}
              t={t}
              sectionRefs={{ hero: heroRef, kpi: kpiRef, tasks: tasksRef, members: membersRef, calendar: calendarRef, budget: budgetRef, similar: similarRef }}
            />
          </div>
      </section>

      {showMembersModal && (
        <MembersModal projectId={projectId} onClose={() => setShowMembersModal(false)} />
      )}
    </>
  );
}
